import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { QRCodeCanvas } from 'qrcode.react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { couponsPathTo } from '../utils/Api';
import { couponShareUrl } from '../utils/coupons';
import SnackbarContext from '../contexts/SnackbarContext';
import FormBox from './building_blocks/FormBox';
import PageHeader from './building_blocks/PageHeader';
import { currency } from './building_blocks/Currency';

const blankCoupon = {
  name: '',
  discountReais: '10',
  expires_at: null,
  max_redemptions: '50',
};

const toMomentDateTime = (value) => {
  const date = moment(value);
  return date.isValid() ? date : null;
};

const toApiDateTime = (value) => (value && value.isValid() ? value.toISOString() : null);

const CouponForm = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const { openSnackbar } = useContext(SnackbarContext);
  const [coupon, setCoupon] = useState(blankCoupon);
  const [savedCoupon, setSavedCoupon] = useState(null);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const qrRef = useRef(null);

  const shareUrl = savedCoupon?.code ? couponShareUrl(savedCoupon.code) : '';

  useEffect(() => {
    document.title = isEditing ? 'Cupom - TudoPets.app' : 'Novo cupom - TudoPets.app';
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) return;

    couponsPathTo
      .get(id)
      .then((result) => {
        const loaded = result.data;
        setSavedCoupon(loaded);
        setCoupon({
          name: loaded.name,
          discountReais: String(Math.round(Number(loaded.discount_value || 0) / 100)),
          expires_at: toMomentDateTime(loaded.expires_at),
          max_redemptions: String(loaded.max_redemptions || ''),
          status: loaded.status,
        });
      })
      .catch(() => {
        openSnackbar('Não foi possível carregar o cupom.', 'error');
        navigate('/control-center/coupons');
      })
      .finally(() => setLoading(false));
  }, [id, isEditing, navigate, openSnackbar]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setCoupon({ ...coupon, [name]: value });
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      openSnackbar('Link copiado.', 'success');
    } catch (error) {
      openSnackbar('Não foi possível copiar o link.', 'error');
    }
  };

  const handleDownloadQr = () => {
    const canvas = qrRef.current;
    if (!canvas || !savedCoupon?.code) return;

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `cupom-${savedCoupon.code}.png`;
    link.click();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      if (isEditing) {
        const result = await couponsPathTo.update(id, {
          name: coupon.name,
          expires_at: toApiDateTime(coupon.expires_at),
          max_redemptions: Number(coupon.max_redemptions || 0),
        });
        setSavedCoupon(result.data);
        openSnackbar('Cupom atualizado.', 'success');
      } else {
        const discountReais = parseInt(coupon.discountReais, 10);
        if (!Number.isInteger(discountReais) || discountReais < 1) {
          openSnackbar('O desconto precisa ser um número inteiro de reais, maior que zero.', 'error');
          setSaving(false);
          return;
        }

        const result = await couponsPathTo.save({
          name: coupon.name,
          discount_type: 'fixed_cents',
          discount_value: discountReais * 100,
          expires_at: toApiDateTime(coupon.expires_at),
          max_redemptions: Number(coupon.max_redemptions || 0),
        });
        openSnackbar('Cupom criado.', 'success');
        navigate(`/control-center/coupons/${result.data.id}`);
      }
    } catch (error) {
      openSnackbar(error?.response?.data?.errors?.[0] || 'Não foi possível salvar o cupom.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Typography>Carregando cupom...</Typography>;
  }

  return (
    <FormBox>
      <Box display="flex" flexDirection="column" gap={2}>
        <PageHeader
          title={isEditing ? 'Cupom' : 'Novo cupom'}
          onBack={() => navigate('/control-center/coupons')}
        />

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              name="name"
              label="Nome"
              value={coupon.name}
              onChange={handleChange}
              helperText="Rótulo interno, ex.: Panfleto outubro"
              fullWidth
              required
            />

            {isEditing ? (
              <TextField
                label="Desconto"
                value={savedCoupon ? currency(savedCoupon.discount_value) : ''}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            ) : (
              <TextField
                name="discountReais"
                label="Desconto (reais inteiros)"
                type="number"
                value={coupon.discountReais}
                onChange={handleChange}
                inputProps={{ min: 1, step: 1 }}
                helperText="Ex.: 10 para R$ 10 de desconto"
                fullWidth
                required
              />
            )}

            <MobileDateTimePicker
              label="Vencimento"
              value={coupon.expires_at}
              onChange={(value) => setCoupon({ ...coupon, expires_at: value })}
              format="DD/MM/YYYY HH:mm"
              ampm={false}
              closeOnSelect={false}
              sx={{ width: '100%', bgcolor: '#F5F8FA', borderRadius: '24px' }}
              slotProps={{
                actionBar: {
                  actions: ['cancel', 'accept'],
                },
                textField: { required: true },
              }}
            />

            <TextField
              name="max_redemptions"
              label="Máximo de usos"
              type="number"
              value={coupon.max_redemptions}
              onChange={handleChange}
              inputProps={{ min: savedCoupon?.redemptions_count || 1, step: 1 }}
              helperText={
                savedCoupon
                  ? `Já resgatado ${savedCoupon.redemptions_count} vez(es)`
                  : 'Quantas contas podem resgatar este cupom'
              }
              fullWidth
              required
            />

            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" type="button" onClick={() => navigate('/control-center/coupons')}>
                Voltar
              </Button>
              <Button variant="contained" type="submit" disabled={saving}>
                {saving ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar cupom'}
              </Button>
            </Box>
          </Stack>
        </form>

        {savedCoupon && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Typography variant="h6">Link e QR</Typography>
            <TextField
              label="Código"
              value={savedCoupon.code}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Link"
              value={shareUrl}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button variant="contained" onClick={handleCopyLink}>
                Copiar link
              </Button>
              <Button variant="outlined" onClick={handleDownloadQr}>
                Baixar QR (PNG)
              </Button>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: '#fff', borderRadius: 2 }}>
              <QRCodeCanvas ref={qrRef} value={shareUrl} size={240} marginSize={4} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Usos: {savedCoupon.redemptions_count}/{savedCoupon.max_redemptions}
              {savedCoupon.status === 'disabled' ? ' · Desabilitado' : ''}
            </Typography>
          </Stack>
        )}
      </Box>
    </FormBox>
  );
};

export default CouponForm;
